import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Wallet,
  Trophy,
  Gift,
  Users,
  History,
  Receipt,
  Copy,
  Check,
  Plus,
  Share2,
  ArrowLeft,
  Coins,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceSettings from '../components/VoiceSettings';

const BACKEND_URL = window.location.origin;

const PlayerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [referrals, setReferrals] = useState({ referred_users: [] });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('fokatmain_token');
    const userData = localStorage.getItem('fokatmain_user');
    
    if (!token || !userData) {
      toast.error('Please login to view dashboard');
      navigate('/auth');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data
      const [profileRes, gameHistoryRes, paymentHistoryRes, referralsRes, walletTransRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/player/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/player/game-history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/player/payment-history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/player/referrals`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/player/wallet/transactions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data);
        // Update user data with latest info
        setUser(data.user);
        localStorage.setItem('fokatmain_user', JSON.stringify(data.user));
      }
      
      if (gameHistoryRes.ok) {
        const data = await gameHistoryRes.json();
        setGameHistory(data.games);
      }
      
      if (paymentHistoryRes.ok) {
        const data = await paymentHistoryRes.json();
        setPaymentHistory(data.payments);
      }
      
      if (referralsRes.ok) {
        const data = await referralsRes.json();
        setReferrals(data);
      }
      
      if (walletTransRes.ok) {
        const data = await walletTransRes.json();
        setWalletTransactions(data.transactions);
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopiedCode(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleAddMoney = async (amount) => {
    try {
      const token = localStorage.getItem('fokatmain_token');
      const response = await fetch(`${BACKEND_URL}/api/player/wallet/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowAddMoneyModal(false);
        // Refresh dashboard data
        fetchDashboardData(token);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add money');
      }
    } catch (error) {
      console.error('Error adding money:', error);
      toast.error('Failed to add money to wallet');
    }
  };

  const handleRedeemPoints = async () => {
    if (user?.points < 10) {
      toast.error('You need at least 10 points to redeem');
      return;
    }

    try {
      const token = localStorage.getItem('fokatmain_token');
      const response = await fetch(`${BACKEND_URL}/api/player/wallet/redeem-points`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ points_to_redeem: 10 })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh dashboard data
        fetchDashboardData(token);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to redeem points');
      }
    } catch (error) {
      console.error('Error redeeming points:', error);
      toast.error('Failed to redeem points');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-yellow-400 text-2xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="gaming-button-secondary"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-yellow-400 text-center">
            Player Dashboard
          </h1>
          <div className="w-32"></div>
        </div>

        {/* Profile Card */}
        <Card className="gaming-card mb-6 bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border-yellow-500/30">
          <CardHeader>
            <CardTitle className="text-yellow-400 flex items-center gap-2">
              <Trophy className="h-6 w-6" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Name</p>
                <p className="text-white font-semibold">{user?.name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Email</p>
                <p className="text-white font-semibold text-sm">{user?.email}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Games</p>
                <p className="text-white font-semibold">{profile?.stats?.total_games_played || 0}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Rank</p>
                <p className="text-white font-semibold">{profile?.stats?.current_rank || 'Unranked'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wallet and Points Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Wallet Card */}
          <Card className="gaming-card bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-green-500/30">
            <CardHeader>
              <CardTitle className="text-green-400 flex items-center gap-2">
                <Wallet className="h-6 w-6" />
                Wallet Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <p className="text-5xl font-bold text-white mb-2">
                  ₹{user?.wallet_balance?.toFixed(2) || '0.00'}
                </p>
                <p className="text-gray-400 text-sm">1 game = ₹10 (3 attempts)</p>
              </div>
              <Button
                className="w-full gaming-button"
                onClick={() => setShowAddMoneyModal(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Money
              </Button>
            </CardContent>
          </Card>

          {/* Points Card */}
          <Card className="gaming-card bg-gradient-to-br from-purple-900/20 to-pink-900/20 border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-purple-400 flex items-center gap-2">
                <Coins className="h-6 w-6" />
                Reward Points
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <p className="text-5xl font-bold text-white mb-2">
                  {user?.points || 0}
                </p>
                <p className="text-gray-400 text-sm">Earn 2 points per referral</p>
                <p className="text-gray-400 text-sm">10 points = ₹10 game credit</p>
              </div>
              <Button
                className="w-full gaming-button"
                disabled={user?.points < 10}
                onClick={handleRedeemPoints}
              >
                <Gift className="mr-2 h-4 w-4" />
                Redeem 10 Points
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Referral Section */}
        <Card className="gaming-card mb-6 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-cyan-900/20 border-purple-500/30 shadow-xl">
          <CardHeader className="border-b border-purple-500/20">
            <CardTitle className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400 flex items-center gap-2 text-2xl">
              <Users className="h-7 w-7 text-purple-400" />
              Refer & Earn Rewards
            </CardTitle>
            <p className="text-gray-400 text-sm mt-2">Invite friends and earn bonus points for every successful referral!</p>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 p-4 rounded-lg border border-purple-500/20 text-center">
                <Users className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs mb-1">Total Referrals</p>
                <p className="text-white text-3xl font-bold">{user?.total_referrals || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 p-4 rounded-lg border border-blue-500/20 text-center">
                <Trophy className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs mb-1">Points Earned</p>
                <p className="text-blue-400 text-3xl font-bold">{user?.points || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 p-4 rounded-lg border border-cyan-500/20 text-center">
                <Gift className="h-6 w-6 text-cyan-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs mb-1">Next Reward At</p>
                <p className="text-cyan-400 text-3xl font-bold">{Math.ceil(((user?.total_referrals || 0) + 1) / 5) * 5}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6 bg-gray-800/50 p-4 rounded-lg border border-purple-500/20">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-400">Progress to Next Milestone</span>
                <span className="text-sm font-semibold text-purple-400">
                  {(user?.total_referrals || 0) % 5}/5 referrals
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-500 to-cyan-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${((user?.total_referrals || 0) % 5) * 20}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Earn bonus rewards every 5 referrals!</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Referral Code & Link */}
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Your Referral Code</label>
                  <div className="flex items-center gap-2 bg-black/40 p-4 rounded-lg border border-purple-500/30">
                    <code className="text-yellow-400 text-2xl font-bold flex-1 tracking-wider">
                      {user?.referral_code}
                    </code>
                    <Button
                      size="sm"
                      onClick={handleCopyReferralCode}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {copiedCode ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Referral Link</label>
                  <div className="flex items-center gap-2 bg-black/40 p-3 rounded-lg border border-cyan-500/30">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/signup?ref=${user?.referral_code}`}
                      className="bg-transparent text-cyan-400 text-sm flex-1 outline-none"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${user?.referral_code}`);
                        toast.success('Referral link copied!');
                      }}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Share Buttons */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Share Via</label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        const text = `Join Fokatmain and win amazing gadgets! Use my referral code: ${user?.referral_code}`;
                        const url = `${window.location.origin}/signup?ref=${user?.referral_code}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        const text = `Join Fokatmain and win amazing gadgets! Use my referral code: ${user?.referral_code}`;
                        const url = `${window.location.origin}/signup?ref=${user?.referral_code}`;
                        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Share2 className="h-4 w-4 mr-1" />
                      Twitter
                    </Button>
                  </div>
                </div>

                {/* Rewards Info */}
                <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-600/30 rounded-lg p-4">
                  <h4 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2">
                    <Gift className="h-5 w-5" />
                    Referral Rewards
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      <span><strong>2 points</strong> for each friend who signs up</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      <span><strong>5 bonus points</strong> at every 5 referrals</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">🎁</span>
                      <span><strong>Exclusive prizes</strong> for top referrers</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column - Referred Friends */}
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Your Referred Friends ({referrals.referred_users?.length || 0})</label>
                <div className="bg-black/40 rounded-lg border border-purple-500/30 max-h-96 overflow-y-auto">
                  {referrals.referred_users?.length > 0 ? (
                    <div className="divide-y divide-gray-700/50">
                      {referrals.referred_users.map((friend, idx) => (
                        <div key={idx} className="p-4 hover:bg-purple-900/10 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                                {friend.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-white font-semibold">{friend.name || 'Anonymous'}</p>
                                <p className="text-gray-500 text-xs">{friend.email}</p>
                                <p className="text-gray-600 text-xs mt-1">
                                  Joined {new Date(friend.created_at || Date.now()).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border-green-500/50">
                              +2 pts
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-12 text-center">
                      <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">No referrals yet</p>
                      <p className="text-gray-600 text-sm">Share your referral code or link to start earning!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Game History */}
        <Card className="gaming-card mb-6">
          <CardHeader>
            <CardTitle className="text-yellow-400 flex items-center gap-2">
              <History className="h-6 w-6" />
              Game History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Game</th>
                    <th className="text-center py-3 px-2 text-gray-400 font-medium">Score</th>
                    <th className="text-center py-3 px-2 text-gray-400 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {gameHistory.length > 0 ? (
                    gameHistory.map((game, idx) => (
                      <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                        <td className="py-3 px-2 text-white">{game.game_name}</td>
                        <td className="py-3 px-2 text-center">
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500">
                            {game.score}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-gray-400 text-sm text-center">
                          {formatDate(game.timestamp)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-8 text-gray-500">
                        No games played yet. Start playing!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="gaming-card">
          <CardHeader>
            <CardTitle className="text-yellow-400 flex items-center gap-2">
              <Receipt className="h-6 w-6" />
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Type</th>
                    <th className="text-center py-3 px-2 text-gray-400 font-medium">Amount</th>
                    <th className="text-center py-3 px-2 text-gray-400 font-medium">Balance</th>
                    <th className="text-center py-3 px-2 text-gray-400 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {walletTransactions.length > 0 ? (
                    walletTransactions.map((txn, idx) => (
                      <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                        <td className="py-3 px-2 text-white">
                          {txn.description}
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={txn.amount > 0 ? 'text-green-400' : 'text-red-400'}>
                            {txn.amount > 0 ? '+' : ''}₹{Math.abs(txn.amount).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center text-white">
                          ₹{txn.balance_after.toFixed(2)}
                        </td>
                        <td className="py-3 px-2 text-gray-400 text-sm text-center">
                          {formatDate(txn.created_at)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-gray-500">
                        No transactions yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Voice Settings */}
        <VoiceSettings />
      </div>

      {/* Add Money Modal */}
      {showAddMoneyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="gaming-card max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-yellow-400">Add Money to Wallet</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">Select amount to add:</p>
              <div className="grid grid-cols-2 gap-4">
                {[10, 20, 30, 40].map((amount) => (
                  <Button
                    key={amount}
                    className="gaming-button h-20 text-2xl"
                    onClick={() => handleAddMoney(amount)}
                  >
                    ₹{amount}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                className="w-full mt-4 gaming-button-secondary"
                onClick={() => setShowAddMoneyModal(false)}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PlayerDashboard;
